import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  CircularProgress,
  MenuItem,
  Avatar,
} from '@mui/material';
import { CloudUpload, YouTube, Close } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const UploadVideoModal = ({
  open,
  onClose,
  onRefresh,
  user,
  videoToEdit = null,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [intervenants, setIntervenants] = useState([]);
  const [loadingIntervenants, setLoadingIntervenants] = useState(false);

  const [formData, setFormData] = useState({
    name_video_str: '',
    description_video_str: '',
    is_published_video_str: true,
    is_paid_video_str: false,
    unlocks_tokens: 0,
    youtube_link: '',
    selected_intervenant_id: user?.id_user || '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const role = Number(user?.role);
  const isAdmin = [1, 2].includes(role);
  const rolePath = isAdmin ? 'admin' : 'intervenant';
  const isEdit = Boolean(videoToEdit);

  // ✅ EFFET POUR REMPLIR LE FORMULAIRE EN MODE EDITION
  useEffect(() => {
    if (isEdit && open) {
      const isYT =
        videoToEdit.file_name_lbrv?.includes('youtube.com') ||
        videoToEdit.file_name_lbrv?.includes('youtu.be');
      setTabValue(isYT ? 1 : 0);
      setFormData({
        name_video_str: videoToEdit.name_video_lbrv || '',
        description_video_str: videoToEdit.description_video_lbrv || '',
        is_published_video_str: videoToEdit.is_published_video_lbrv === 1,
        is_paid_video_str: videoToEdit.is_paid_video_lbrv === 1,
        unlocks_tokens: videoToEdit.unlocks_tokens || 0,
        youtube_link: isYT ? videoToEdit.file_name_lbrv : '',
        selected_intervenant_id:
          videoToEdit.id_intervenant_lbrv || user?.id_user,
      });
    } else if (!isEdit && open) {
      // Reset si c'est une création
      setFormData({
        name_video_str: '',
        description_video_str: '',
        is_published_video_str: true,
        is_paid_video_str: false,
        unlocks_tokens: 0,
        youtube_link: '',
        selected_intervenant_id: user?.id_user || '',
      });
      setSelectedFile(null);
    }
  }, [videoToEdit, open, isEdit, user]);

  // Charger les intervenants pour l'admin
  useEffect(() => {
    if (open && isAdmin) {
      const fetchIntervenants = async () => {
        setLoadingIntervenants(true);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/admin/intervenant/l/list_intervenant`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );

          setIntervenants(res.data.result || []);
        } catch (err) {
          console.error('Erreur chargement intervenants:', err);
          toast.error('Impossible de charger la liste');
        } finally {
          setLoadingIntervenants(false);
        }
      };

      fetchIntervenants();
    }
  }, [open, isAdmin]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // =========================
      // VALIDATION FRONT
      // =========================
      if (!formData.name_video_str || !formData.description_video_str) {
        toast.error('Titre et description obligatoires');
        return;
      }

      const hasFile = selectedFile instanceof File;
      const hasYoutube = formData.youtube_link?.trim()?.length > 0;

      if (!hasFile && !hasYoutube && !isEdit) {
        toast.error('Veuillez fournir une vidéo ou un lien YouTube');
        return;
      }

      const data = new FormData();

      data.append('name_video_str', formData.name_video_str);
      data.append('description_video_str', formData.description_video_str);
      data.append('is_paid_video_str', formData.is_paid_video_str ? '1' : '0');
      data.append(
        'is_published_video_str',
        formData.is_published_video_str ? '1' : '0'
      );
      data.append('unlocks_tokens', formData.unlocks_tokens ?? 0);

      // =========================
      // VIDEO LOGIC CLEAN
      // =========================
      if (tabValue === 1 && hasYoutube) {
        data.append('youtube_link', formData.youtube_link.trim());
      }

      if (tabValue === 0 && hasFile) {
        data.append('file', selectedFile);
      }

      const targetId = isAdmin
        ? formData.selected_intervenant_id
        : user?.id_user;

      const baseUrl = `${process.env.REACT_APP_API_URL}/${rolePath}`;

      const url = isEdit
        ? `${baseUrl}/streaming/public/videos/u/update/${videoToEdit.id_video_lbrv}`
        : `${baseUrl}/streaming/public/upload/video/${targetId}`;

      // =========================
      // IMPORTANT FIX AXIOS
      // =========================
      const res = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          // ❌ NE PAS mettre Content-Type
        },
      });

      toast.success(
        res?.data?.message || (isEdit ? 'Vidéo modifiée' : 'Vidéo publiée')
      );

      onRefresh();
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.first_error ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Erreur upload vidéo';

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '28px' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          p: 3,
          gap: 1,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#1B2559' }}
          >
            {isEdit ? 'Modifier la vidéo' : 'Publier une vidéo'}
          </Typography>

          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* 🔥 TEXTE INFORMATIF */}
        <Typography
          sx={{
            fontSize: '0.85rem',
            color: '#A3AED0',
            lineHeight: 1.4,
            maxWidth: '90%',
          }}
        >
          Vous pouvez soit <strong>uploader une vidéo</strong> (MP4, MOV, etc.),
          soit ajouter un <strong>lien YouTube</strong>. Les fichiers volumineux
          peuvent prendre du temps à être envoyés.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ mb: 3 }}
        >
          <Tab icon={<CloudUpload />} label="Fichier" iconPosition="start" />
          <Tab icon={<YouTube />} label="YouTube" iconPosition="start" />
        </Tabs>

        <Stack spacing={2.5}>
          {isAdmin && (
            <TextField
              select
              fullWidth
              label="Affecter à l'intervenant"
              name="selected_intervenant_id"
              value={formData.selected_intervenant_id}
              onChange={handleChange}
              disabled={loadingIntervenants}
            >
              {intervenants.map((int) => (
                <MenuItem key={int.id_user} value={int.id_user}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={`${process.env.REACT_APP_API_URL}/storage/workshop/${int.photo_user}`}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography>
                      {int.prenom_user} {int.nom_user}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            label="Titre"
            name="name_video_str"
            value={formData.name_video_str}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            name="description_video_str"
            value={formData.description_video_str}
            onChange={handleChange}
          />

          {tabValue === 0 ? (
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ py: 2, borderStyle: 'dashed', borderRadius: '15px' }}
            >
              {selectedFile
                ? selectedFile.name
                : isEdit
                  ? 'Changer la vidéo (optionnel)'
                  : 'Choisir la vidéo MP4'}
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </Button>
          ) : (
            <TextField
              fullWidth
              label="Lien YouTube"
              name="youtube_link"
              value={formData.youtube_link}
              onChange={handleChange}
            />
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            p={2}
            sx={{ bgcolor: '#F4F7FE', borderRadius: '15px' }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published_video_str}
                  name="is_published_video_str"
                  onChange={handleChange}
                />
              }
              label="Publier"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_paid_video_str}
                  name="is_paid_video_str"
                  onChange={handleChange}
                />
              }
              label="Premium"
            />
          </Stack>

          {formData.is_paid_video_str && (
            <TextField
              fullWidth
              type="number"
              label="Prix (Jetons)"
              name="unlocks_tokens"
              value={formData.unlocks_tokens}
              onChange={handleChange}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} sx={{ color: '#A3AED0', fontWeight: 700 }}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{ bgcolor: '#4318FF', borderRadius: '12px', px: 4 }}
        >
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} sx={{ color: 'white' }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Upload en cours... veuillez patienter
              </Typography>
            </Stack>
          ) : isEdit ? (
            'Enregistrer'
          ) : (
            'Publier'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadVideoModal;
